import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '@/app/store'
import { setCustomerData, clearCustomerData } from '../customer/customerSlice'
import { csFlowApi } from './csFlowAPI'

// CS Flow State Interface - matches Flutter's CsFlowController
interface CsFlowState {
  // Customer validation state
  customerNumber: string
  customerName: string
  customerId: number
  isCustomerNumberValid: boolean
  isLoadingCustomer: boolean

  // Order type selection
  selectedOrderType: string | null
  isOrderTypeSelected: boolean
  orderTypes: Array<{ id: number; name: string; title: string; icon: string; translationKey: string }>
  isOrderTypesLoading: boolean

  // Address and branch selection
  isAddressSelected: boolean
  isBranchSelected: boolean
  selectedBranchId: number | null

  // Car details for car pickup orders (typeId = 4)
  carColor: string
  carType: string
  carNumber: string
  carBrand: string
  carModel: string
  carLogo: string

  // Car data from API
  carModels: Array<Record<string, any>>
  carColors: Array<Record<string, any>>
  isLoadingCarData: boolean

  // Car color search
  carColorSearchQuery: string

  // Car type search
  carTypeSearchQuery: string

  // Flow Completion
  isCsFlowComplete: boolean
  hasCompletedOrderDetails: boolean // Flag to ensure menu only appears after clicking "Save and Continue"

  // Branch data
  branches: Array<Record<string, any>>
  isLoadingBranches: boolean

  // Saved addresses
  savedAddresses: Array<Record<string, any>>
  isLoadingAddresses: boolean
  selectedAddressId: number | null
  
  // Address popup state
  addressStep: 'noAddresses' | 'savedAddresses' | 'addNewAddress'
  selectedAddressType: string
  apartmentNumber: string
  selectedLatLng: { lat: number; lng: number } | null
  selectedAddressText: string
}

const initialCsFlowState: CsFlowState = {
  // Customer
  customerNumber: '',
  customerName: '',
  customerId: 0,
  isCustomerNumberValid: false,
  isLoadingCustomer: false,

  // Order Type
  selectedOrderType: null,
  isOrderTypeSelected: false,
  orderTypes: [],
  isOrderTypesLoading: false,

  // Address and branch
  isAddressSelected: false,
  isBranchSelected: false,
  selectedBranchId: null,

  // Car details
  carColor: '',
  carType: '',
  carNumber: '',
  carBrand: '',
  carModel: '',
  carLogo: '',

  // Car data
  carModels: [],
  carColors: [],
  isLoadingCarData: false,

  // Car search
  carColorSearchQuery: '',
  carTypeSearchQuery: '',

  // Flow Completion
  isCsFlowComplete: false,
  hasCompletedOrderDetails: false, // Menu should only appear after clicking "Save and Continue"

  // Branch data
  branches: [],
  isLoadingBranches: false,

  // Address data
  savedAddresses: [],
  isLoadingAddresses: false,
  selectedAddressId: null,
  
  // Address popup state
  addressStep: 'noAddresses',
  selectedAddressType: 'home',
  apartmentNumber: '',
  selectedLatLng: null,
  selectedAddressText: '',
}

// Async thunk to validate customer number
export const validateCustomerNumber = createAsyncThunk(
  'csFlow/validateCustomerNumber',
  async (phoneNumber: string, { dispatch, rejectWithValue }) => {
    try {
      // Force refetch to avoid using cached data from previous customer
      const response = await csFlowApi.endpoints.searchCustomerByPhone.initiate(
        { phone: phoneNumber.trim() },
        { forceRefetch: true }
      )
      
      if ('data' in response && response.data) {
        const responseData = response.data as any
        if (responseData.data && responseData.data.length > 0) {
          const customer = responseData.data[0]
        
        // Update customer data in customer slice
        dispatch(setCustomerData({
          id: customer.id,
          name: customer.name || `Customer ${phoneNumber}`,
          phone: phoneNumber.trim(),
        }))

        // Load cart details for customer (with force refresh to avoid cached data)
        await dispatch(loadCartDetailsForCustomer(customer.id))

          return {
            customerId: customer.id,
            customerName: customer.name || `Customer ${phoneNumber}`,
            customerPhone: phoneNumber.trim(),
          }
        }
      }
      return rejectWithValue('Customer not found')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to validate customer')
    }
  }
)

// Async thunk to load cart details and apply to flow
export const loadCartDetailsForCustomer = createAsyncThunk(
  'csFlow/loadCartDetailsForCustomer',
  async (customerId: number, { dispatch, getState }) => {
    try {
      const state = getState() as RootState
      const couponCode = state.discount?.discountCode?.trim()
      // Force refetch to avoid using cached data from previous customer
      const response = await csFlowApi.endpoints.getCsCartData.initiate(
        { customerId, couponCode },
        { forceRefetch: true }
      )
      
      if ('data' in response && response.data) {
        const responseData = response.data as any
        if (responseData.data) {
          const cartData = responseData.data
        
          // Apply cart details to flow
          if (cartData.type_id) {
            dispatch(updateOrderTypeFromId(cartData.type_id))
          }
          
          if (cartData.branch_id) {
            dispatch(markBranchSelected(cartData.branch_id))
          }
          
          if (cartData.address_id && cartData.type_id === 1) {
            dispatch(markAddressSelected())
            dispatch(setSelectedAddressId(cartData.address_id))
          }
        
          // Save branch data to localStorage
          if (cartData.branch) {
            localStorage.setItem('selected_branch_data', JSON.stringify(cartData.branch))
          }
        
          return cartData
        }
      }
    } catch (error) {
      // Ignore errors - branch will remain unselected
    }
  }
)

// Async thunk to load order types
export const loadOrderTypes = createAsyncThunk(
  'csFlow/loadOrderTypes',
  async (_, { rejectWithValue }) => {
    try {
      const employeeDataStr = localStorage.getItem('employee_data')
      if (!employeeDataStr) {
        return []
      }

      const employeeData = JSON.parse(employeeDataStr)
      const apiOrderTypes = employeeData.orderTypes || []

      const convertedOrderTypes = apiOrderTypes.map((orderType: any) => {
        const id = orderType.id
        const name = orderType.name
        // Use icon from API if available, otherwise fallback to local icon
        const icon = orderType.icon || orderType.image || getIconForOrderType(id, name)
        const translationKey = getTranslationKeyForOrderType(id, name)

        return {
          id,
          name,
          title: translationKey, // Will be translated in component
          icon,
          translationKey,
        }
      })

      return convertedOrderTypes
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load order types')
    }
  }
)

// Helper function to get icon for order type
function getIconForOrderType(id: number, name: string): string {
  switch (id) {
    case 1:
      return '../assets/images/talbet.svg' // Delivery
    case 2:
      return '../assets/images/Frame1.svg' // Takeaway
    case 3:
      return '../assets/images/Table.svg' // Dine In
    case 4:
      return '../assets/images/car.svg' // Car Pickup
    default:
      const lowerName = name.toLowerCase()
      if (lowerName.includes('توصيل') || lowerName.includes('delivery')) {
        return '../assets/images/talbet.svg'
      } else if (lowerName.includes('سفري') || lowerName.includes('takeaway') || lowerName.includes('take away')) {
        return '../assets/images/Frame1.svg'
      } else if (lowerName.includes('محلي') || lowerName.includes('dine') || lowerName.includes('local') || lowerName.includes('table')) {
        return '../assets/images/Table.svg'
      } else if (lowerName.includes('استلام من السيارة') || lowerName.includes('car pickup') || lowerName.includes('car_pickup')) {
        return '../assets/images/car.svg'
      } else {
        return '../assets/images/order.svg'
      }
  }
}

// Helper function to get translation key for order type
function getTranslationKeyForOrderType(id: number, name: string): string {
  switch (id) {
    case 1:
      return 'order_type_delivery'
    case 2:
      return 'order_type_takeaway'
    case 3:
      return 'order_type_dine_in'
    case 4:
      return 'order_type_car_pickup'
    default:
      const lowerName = name.toLowerCase()
      if (lowerName.includes('توصيل') || lowerName.includes('delivery')) {
        return 'order_type_delivery'
      } else if (lowerName.includes('سفري') || lowerName.includes('takeaway') || lowerName.includes('take away')) {
        return 'order_type_takeaway'
      } else if (lowerName.includes('محلي') || lowerName.includes('dine') || lowerName.includes('local') || lowerName.includes('table')) {
        return 'order_type_dine_in'
      } else if (lowerName.includes('استلام من السيارة') || lowerName.includes('car pickup') || lowerName.includes('car_pickup')) {
        return 'order_type_car_pickup'
      }
      return 'order_type_takeaway'
  }
}

// Async thunk to change order type
export const changeOrderType = createAsyncThunk(
  'csFlow/changeOrderType',
  async (params: { typeId: number; customerId: number; branchId: number; addressId?: number }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState() as RootState
      const cartId = state.cartItems.cartInfo?.id?.toString() || localStorage.getItem('cs_cart_id') || ''

      if (!cartId) {
        // If no cart, just update the order type locally
        return { typeId: params.typeId, success: true }
      }

      const requestData: any = {
        userId: params.customerId,
        typeId: params.typeId,
        branchId: params.branchId,
      }

      if (params.typeId === 1 && params.addressId) {
        requestData.addressId = params.addressId
      }

      const response = await csFlowApi.endpoints.changeCartType.initiate({
        cartId,
        userId: requestData.userId,
        typeId: requestData.typeId,
        branchId: requestData.branchId,
        addressId: requestData.addressId,
      })

      if ('data' in response && response.data) {
        return { typeId: params.typeId, success: true }
      } else {
        return rejectWithValue('Failed to change order type')
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to change order type')
    }
  }
)

// Async thunk to load branches
export const loadBranches = createAsyncThunk(
  'csFlow/loadBranches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await csFlowApi.endpoints.getEmployeeBranches.initiate({})
      
      if ('data' in response && response.data) {
        const responseData = response.data as any
        if (responseData.data && responseData.data.branches) {
          const branches = responseData.data.branches.map((branch: any) => ({
          id: branch.id,
          name: branch.name,
          descriptiveAddress: branch.descriptive_address || '',
          display_name: branch.name,
          area: '',
          city: '',
          active: branch.active || false,
          busy: branch.busy || false,
          is_open: branch.is_open || false,
          working_from: branch.working_from || '',
          working_to: branch.working_to || '',
          is_available: (branch.active || false) && !(branch.busy || false) && (branch.is_open || false),
        }))

          return branches
        }
      }
      return []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load branches')
    }
  }
)

// Async thunk to load car data
export const loadCarData = createAsyncThunk(
  'csFlow/loadCarData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await csFlowApi.endpoints.getCarModelsAndColors.initiate({})
      
      if ('data' in response && response.data) {
        const responseData = response.data as any
        if (responseData.data) {
          return {
            models: responseData.data.models || [],
            colors: responseData.data.colors || [],
          }
        }
      }
      return { models: [], colors: [] }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load car data')
    }
  }
)

// Async thunk to load saved addresses
export const loadSavedAddresses = createAsyncThunk(
  'csFlow/loadSavedAddresses',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await csFlowApi.endpoints.getCustomerAddresses.initiate({ userId })
      
      if ('data' in response && response.data) {
        const responseData = response.data as any
        if (responseData.data) {
          return responseData.data
        }
      }
      return []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load addresses')
    }
  }
)

const csFlowSlice = createSlice({
  name: 'csFlow',
  initialState: initialCsFlowState,
  reducers: {
    // Update customer number
    updateCustomerNumber: (state, action: PayloadAction<string>) => {
      const trimmedPhone = action.payload.trim()
      const previousCustomerId = state.customerId
      
      // If customer number changed, clear old customer data
      if (state.customerNumber !== trimmedPhone && previousCustomerId > 0) {
        state.customerName = ''
        state.customerId = 0
        state.isCustomerNumberValid = false
      }
      
      state.customerNumber = trimmedPhone
      state.isCustomerNumberValid = false
      state.hasCompletedOrderDetails = false // Reset when customer number changes
      
      if (trimmedPhone.length === 0) {
        state.customerNumber = ''
        state.customerName = ''
        state.customerId = 0
        state.isCustomerNumberValid = false
        state.isLoadingCustomer = false
      }
      
    },

    // Update customer data
    updateCustomerData: (state, action: PayloadAction<{ phone: string; name: string; id: number }>) => {
      // If customer ID changed, reset order details completion
      const customerChanged = state.customerId !== action.payload.id
      
      state.customerNumber = action.payload.phone
      state.customerName = action.payload.name
      state.customerId = action.payload.id
      state.isCustomerNumberValid = true
      
      // Reset order details completion if customer changed
      if (customerChanged) {
        state.hasCompletedOrderDetails = false
      }
      
      // Don't clear order selections when customer is set - let user complete the flow
      // Only clear if there's no existing order type selected
      // This allows the flow to work properly: customer -> order details -> menu
      
    },

    // Select order type
    selectOrderType: (state, action: PayloadAction<string>) => {
      let orderType = action.payload
      
      // Handle numeric IDs
      if (orderType === '1' || orderType === '2' || orderType === '3' || orderType === '4') {
        switch (orderType) {
          case '1':
            orderType = 'delivery'
            break
          case '2':
            orderType = 'take away'
            break
          case '3':
            orderType = 'dine-in'
            break
          case '4':
            orderType = 'car pickup'
            break
          default:
            orderType = 'delivery'
        }
      }
      
      state.selectedOrderType = orderType
      state.isOrderTypeSelected = true
      state.isAddressSelected = false
      
      // Auto-select default address for delivery orders
      if (orderType.toLowerCase().includes('delivery') || orderType.toLowerCase().includes('توصيل')) {
        state.isAddressSelected = true
      }
      
    },

    // Update order type from ID
    updateOrderTypeFromId: (state, action: PayloadAction<number>) => {
      const typeId = action.payload
      
      switch (typeId) {
        case 1:
          state.selectedOrderType = 'delivery'
          break
        case 2:
          state.selectedOrderType = 'take away'
          break
        case 3:
          state.selectedOrderType = 'dine-in'
          break
        case 4:
          state.selectedOrderType = 'car pickup'
          break
        default:
          state.selectedOrderType = 'delivery'
      }
      
      // CRITICAL: Set isOrderTypeSelected to true
      state.isOrderTypeSelected = true
      state.isAddressSelected = false
      
      // Ensure address is selected for delivery orders
      if (state.selectedOrderType?.toLowerCase().includes('delivery') || 
          state.selectedOrderType?.toLowerCase().includes('توصيل')) {
        state.isAddressSelected = true
      }
      
    },

    // Mark branch as selected
    markBranchSelected: (state, action: PayloadAction<number | undefined>) => {
      // CRITICAL: Set isBranchSelected to true
      state.isBranchSelected = true
      if (action.payload !== undefined && action.payload !== null) {
        state.selectedBranchId = action.payload
      }
      
      // Reset address selection for delivery orders when branch changes
      if (state.selectedOrderType?.toLowerCase().includes('delivery') || 
          state.selectedOrderType?.toLowerCase().includes('توصيل')) {
        state.isAddressSelected = false
      }
    },

    // Mark address as selected
    markAddressSelected: (state) => {
      state.isAddressSelected = true
      
    },

    // Set selected address ID
    setSelectedAddressId: (state, action: PayloadAction<number>) => {
      state.selectedAddressId = action.payload
      localStorage.setItem('selected_address_id', action.payload.toString())
    },

    // Set car details
    setCarDetails: (state, action: PayloadAction<{
      color: string
      type: string
      number: string
      brand?: string
      model?: string
      logo?: string
    }>) => {
      const trimmedColor = action.payload.color?.trim() || ''
      const trimmedType = action.payload.type?.trim() || ''
      const trimmedNumber = action.payload.number?.trim() || ''
      const hasBrand = action.payload.brand !== undefined
      const brandValue = action.payload.brand ?? ''
      const trimmedBrand = brandValue.trim()
      const hasModel = action.payload.model !== undefined
      const trimmedModel = (action.payload.model ?? '').trim()
      const trimmedLogo = action.payload.logo?.trim() || ''

      state.carColor = trimmedColor
      state.carType = trimmedType
      state.carNumber = trimmedNumber
      
      if (hasBrand) {
        state.carBrand = trimmedBrand

        if (!trimmedBrand) {
          state.carModel = ''
        } else if (!hasModel) {
          state.carModel = trimmedBrand
        }
      }

      if (hasModel) {
        state.carModel = trimmedModel
      }

      state.carLogo = trimmedLogo
      
    },

    // Clear car details
    clearCarDetails: (state) => {
      state.carColor = ''
      state.carType = ''
      state.carNumber = ''
      state.carBrand = ''
      state.carModel = ''
      state.carLogo = ''
      
    },

    // Update car color search
    updateCarColorSearch: (state, action: PayloadAction<string>) => {
      state.carColorSearchQuery = action.payload
      
    },

    // Update car type search
    updateCarTypeSearch: (state, action: PayloadAction<string>) => {
      state.carTypeSearchQuery = action.payload
      
    },

    // Clear all selections
    clearAllSelections: (state) => {
      state.isOrderTypeSelected = false
      state.selectedOrderType = null
      state.isAddressSelected = false
      state.isBranchSelected = false
      state.selectedBranchId = null
      // Clear car details
      state.carColor = ''
      state.carType = ''
      state.carNumber = ''
      state.carBrand = ''
      state.carModel = ''
      state.carLogo = ''
    },

    // Clear branch selection
    clearBranchSelection: (state) => {
      state.isBranchSelected = false
      state.selectedBranchId = null
      localStorage.removeItem('selected_branch_data')
    },

    // Clear address selection
    clearAddressSelection: (state) => {
      state.isAddressSelected = false
      state.selectedAddressId = null
      state.selectedAddressText = ''
      localStorage.removeItem('selected_address_id')
    },

    // Mark order details as completed (called when user clicks "Save and Continue")
    markOrderDetailsCompleted: (state) => {
      state.hasCompletedOrderDetails = true
    },

    // Reset CS flow
    resetCsFlow: (state) => {
      state.customerNumber = ''
      state.customerName = ''
      state.customerId = 0
      state.isCustomerNumberValid = false
      state.selectedOrderType = null
      state.isOrderTypeSelected = false
      state.isAddressSelected = false
      state.isBranchSelected = false
      state.selectedBranchId = null
      state.isLoadingCustomer = false
      state.hasCompletedOrderDetails = false // Reset flag
      // Clear car details
      state.carColor = ''
      state.carType = ''
      state.carNumber = ''
      state.carBrand = ''
      state.carModel = ''
      state.carLogo = ''
      
      // Clear localStorage
      localStorage.removeItem('selected_address_id')
      localStorage.removeItem('selected_branch_data')
      localStorage.removeItem('cs_cart_id')
    },

    // Load saved branch data
    loadSavedBranchData: (state) => {
      try {
        const selectedBranchData = localStorage.getItem('selected_branch_data')
        if (selectedBranchData) {
          const branchData = JSON.parse(selectedBranchData)
          const branchId = branchData.id
          if (branchId) {
            const branchIdInt = typeof branchId === 'number' ? branchId : parseInt(branchId.toString())
            if (!isNaN(branchIdInt)) {
              state.selectedBranchId = branchIdInt
              state.isBranchSelected = true
            }
          }
        }
      } catch (e) {
      }
    },

    // Address popup actions
    setAddressStep: (state, action: PayloadAction<'noAddresses' | 'savedAddresses' | 'addNewAddress'>) => {
      state.addressStep = action.payload
    },

    setSelectedAddressType: (state, action: PayloadAction<string>) => {
      state.selectedAddressType = action.payload
    },

    setApartmentNumber: (state, action: PayloadAction<string>) => {
      state.apartmentNumber = action.payload
    },

    setSelectedLatLng: (state, action: PayloadAction<{ lat: number; lng: number } | null>) => {
      state.selectedLatLng = action.payload
    },

    setSelectedAddressText: (state, action: PayloadAction<string>) => {
      state.selectedAddressText = action.payload
    },

    resetAddressForm: (state) => {
      state.addressStep = 'noAddresses'
      state.selectedAddressType = 'home'
      state.apartmentNumber = ''
      state.selectedLatLng = null
      state.selectedAddressText = ''
    },
  },
  extraReducers: (builder) => {
    builder
      // Validate customer number
      .addCase(validateCustomerNumber.pending, (state) => {
        state.isLoadingCustomer = true
        state.isCustomerNumberValid = false
      })
      .addCase(validateCustomerNumber.fulfilled, (state, action) => {
        state.isLoadingCustomer = false
        state.isCustomerNumberValid = true
        state.customerId = action.payload.customerId
        state.customerName = action.payload.customerName
        state.customerNumber = action.payload.customerPhone
      })
      .addCase(validateCustomerNumber.rejected, (state) => {
        state.isLoadingCustomer = false
        state.isCustomerNumberValid = false
        state.customerNumber = ''
        state.customerName = ''
        state.customerId = 0
      })
      
      // Load order types
      .addCase(loadOrderTypes.pending, (state) => {
        state.isOrderTypesLoading = true
      })
      .addCase(loadOrderTypes.fulfilled, (state, action) => {
        state.isOrderTypesLoading = false
        state.orderTypes = action.payload
      })
      .addCase(loadOrderTypes.rejected, (state) => {
        state.isOrderTypesLoading = false
        state.orderTypes = []
      })
      
      // Load branches
      .addCase(loadBranches.pending, (state) => {
        state.isLoadingBranches = true
      })
      .addCase(loadBranches.fulfilled, (state, action) => {
        state.isLoadingBranches = false
        state.branches = action.payload
      })
      .addCase(loadBranches.rejected, (state) => {
        state.isLoadingBranches = false
        state.branches = []
      })
      
      // Load car data
      .addCase(loadCarData.pending, (state) => {
        state.isLoadingCarData = true
      })
      .addCase(loadCarData.fulfilled, (state, action) => {
        state.isLoadingCarData = false
        state.carModels = action.payload.models
        state.carColors = action.payload.colors
      })
      .addCase(loadCarData.rejected, (state) => {
        state.isLoadingCarData = false
        state.carModels = []
        state.carColors = []
      })
      
      // Load saved addresses
      .addCase(loadSavedAddresses.pending, (state) => {
        state.isLoadingAddresses = true
      })
      .addCase(loadSavedAddresses.fulfilled, (state, action) => {
        state.isLoadingAddresses = false
        state.savedAddresses = action.payload
        // Set address step based on addresses count
        if (action.payload.length === 0) {
          state.addressStep = 'noAddresses'
        } else {
          state.addressStep = 'savedAddresses'
        }
      })
      .addCase(loadSavedAddresses.rejected, (state) => {
        state.isLoadingAddresses = false
        state.savedAddresses = []
        state.addressStep = 'noAddresses'
      })
      
      // Change order type
      .addCase(changeOrderType.fulfilled, (state, action) => {
        // Update order type from ID
        const typeId = action.payload.typeId
        switch (typeId) {
          case 1:
            state.selectedOrderType = 'delivery'
            break
          case 2:
            state.selectedOrderType = 'take away'
            break
          case 3:
            state.selectedOrderType = 'dine-in'
            break
          case 4:
            state.selectedOrderType = 'car pickup'
            break
          default:
            state.selectedOrderType = 'delivery'
        }
        state.isOrderTypeSelected = true
        state.isAddressSelected = false
        
        // Ensure address is selected for delivery orders
        if (state.selectedOrderType?.toLowerCase().includes('delivery') || 
            state.selectedOrderType?.toLowerCase().includes('توصيل')) {
          state.isAddressSelected = true
        }
      })
      
      // Load cart details for customer
      .addCase(loadCartDetailsForCustomer.fulfilled, (state) => {
        // State is already updated by the dispatched actions in the thunk
      })
      .addCase(loadCartDetailsForCustomer.rejected, (state) => {
      })
  },
})

// Selectors
export const selectIsCsFlowComplete = (state: RootState): boolean => {
  const csFlow = state.csFlow
  // Must have customer, order type, branch selected, AND user must have clicked "Save and Continue"
  const checks = {
    isCustomerNumberValid: csFlow.isCustomerNumberValid,
    customerId: csFlow.customerId,
    isOrderTypeSelected: csFlow.isOrderTypeSelected,
    isBranchSelected: csFlow.isBranchSelected,
    selectedBranchId: csFlow.selectedBranchId,
    selectedOrderType: csFlow.selectedOrderType,
    hasCompletedOrderDetails: csFlow.hasCompletedOrderDetails, // CRITICAL: Must click "Save and Continue"
  }
  
  // Check customer validation
  if (!csFlow.isCustomerNumberValid) {
    return false
  }
  
  if (csFlow.customerId === 0) {
    return false
  }
  
  // Check order type selection
  if (!csFlow.isOrderTypeSelected) {
    return false
  }
  
  if (!csFlow.selectedOrderType) {
    return false
  }
  
  // Check branch selection
  if (!csFlow.isBranchSelected) {
    return false
  }
  
  // Check that branch ID is not null or undefined
  if (csFlow.selectedBranchId === null || csFlow.selectedBranchId === undefined) {
    return false
  }
  
  // CRITICAL: Check if user has clicked "Save and Continue"
  if (!csFlow.hasCompletedOrderDetails) {
    return false
  }
  
  return true
}

export const selectSelectedTypeId = (state: RootState): number => {
  const selectedOrderType = state.csFlow.selectedOrderType
  if (!selectedOrderType) return 1
  
  switch (selectedOrderType.toLowerCase()) {
    case 'delivery':
    case 'توصيل':
      return 1
    case 'take away':
    case 'سفري':
      return 2
    case 'dine-in':
    case 'محلي':
      return 3
    case 'car pickup':
    case 'استلام من السيارة':
      return 4
    default:
      return 1
  }
}

export const {
  updateCustomerNumber,
  updateCustomerData,
  selectOrderType,
  updateOrderTypeFromId,
  markBranchSelected,
  markAddressSelected,
  setSelectedAddressId,
  setCarDetails,
  clearCarDetails,
  updateCarColorSearch,
  updateCarTypeSearch,
  clearAllSelections,
  clearBranchSelection,
  clearAddressSelection,
  markOrderDetailsCompleted,
  resetCsFlow,
  loadSavedBranchData,
  setAddressStep,
  setSelectedAddressType,
  setApartmentNumber,
  setSelectedLatLng,
  setSelectedAddressText,
  resetAddressForm,
} = csFlowSlice.actions

export default csFlowSlice.reducer
